import { createFileRoute } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { OrganizationCombobox } from '@apps/web/components/admin/organization_combobox';
import { Badge } from '@apps/web/components/ui/badge';
import { Card } from '@apps/web/components/ui/card';
import { Label } from '@apps/web/components/ui/label';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Textarea } from '@apps/web/components/ui/textarea';
import { modeStore } from '@apps/web/lib/mode_store';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import {
    type ApiClient,
    type ApiQueryParamsType,
    type ApiResponseType,
    createApiClient,
} from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/search')({
    head: () => ({
        meta: [
            {
                title: 'Search | Phoebe Admin',
            },
        ],
    }),
    component: AdminSearchPage,
});

type AdminSearchResponse = ApiResponseType<'/admin/search', 'get'>;
type AdminSearchQueryParams = ApiQueryParamsType<'/admin/search', 'get'>;
type SearchEntityType = NonNullable<
    AdminSearchQueryParams['entity_types']
>[number];
type SearchResult = AdminSearchResponse['results'][number] & {
    readonly hydrated?: Record<string, unknown> | null;
};

type SearchMode = 'live' | 'sandbox';
type CombinedSearchResult = SearchResult & {
    readonly mode: SearchMode;
};
type CombinedSearchResponse = {
    readonly entity_types: ReadonlyArray<SearchEntityType>;
    readonly results: Array<CombinedSearchResult>;
    readonly counts_by_entity: Record<SearchEntityType, number>;
    readonly counts_by_mode: Record<SearchMode, number>;
    readonly errors_by_entity: Record<string, string>;
};

const ENTITY_OPTIONS: ReadonlyArray<{
    readonly value: SearchEntityType;
    readonly label: string;
}> = [
    { value: 'caregivers', label: 'Caregivers' },
    { value: 'voice_calls', label: 'Voice Calls' },
    { value: 'messages', label: 'Messages' },
    { value: 'shifts', label: 'Shifts' },
    { value: 'clients', label: 'Clients' },
];

const SEARCH_MODES: ReadonlyArray<SearchMode> = ['live', 'sandbox'];
const ALL_ENTITY_TYPES: ReadonlyArray<SearchEntityType> = ENTITY_OPTIONS.map(
    (option) => option.value,
);
const modeApiClientByMode = new Map<SearchMode, ApiClient>();

const ENTITY_LABELS: Readonly<Record<SearchEntityType, string>> = {
    caregivers: 'Caregivers',
    voice_calls: 'Voice Calls',
    messages: 'Messages',
    shifts: 'Shifts',
    clients: 'Clients',
};

function AdminSearchPage() {
    const [query, setQuery] = useState('');
    const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<
        Array<string>
    >([]);
    const [isSearching, setIsSearching] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [response, setResponse] = useState<CombinedSearchResponse | null>(
        null,
    );

    const organizationId = selectedOrganizationIds[0];
    const trimmedQuery = query.trim();
    const canSearch = organizationId !== undefined && trimmedQuery.length > 0;

    useEffect(() => {
        if (!canSearch || organizationId === undefined) {
            setResponse(null);
            setIsSearching(false);
            setErrorMessage(null);
            return;
        }

        let isCancelled = false;
        const timeoutId = setTimeout(() => {
            void (async () => {
                setIsSearching(true);
                setErrorMessage(null);

                try {
                    const responses = await Promise.all(
                        SEARCH_MODES.map((mode) =>
                            fetchModeSearch({
                                mode,
                                query: trimmedQuery,
                                organizationId,
                            }),
                        ),
                    );

                    if (isCancelled) {
                        return;
                    }

                    setResponse(
                        combineSearchResponses({
                            responses,
                        }),
                    );
                } catch (error: unknown) {
                    if (isCancelled) {
                        return;
                    }
                    setResponse(null);
                    setErrorMessage(getErrorMessage(error));
                } finally {
                    if (!isCancelled) {
                        setIsSearching(false);
                    }
                }
            })();
        }, 300);

        return () => {
            isCancelled = true;
            clearTimeout(timeoutId);
        };
    }, [canSearch, organizationId, trimmedQuery]);

    const resultsByEntity = useMemo(() => {
        const grouped = new Map<
            SearchEntityType,
            Array<CombinedSearchResult>
        >();
        for (const entityType of ALL_ENTITY_TYPES) {
            grouped.set(entityType, []);
        }
        if (response === null) {
            return grouped;
        }
        for (const result of response.results) {
            const existing = grouped.get(result.entity_type) ?? [];
            grouped.set(result.entity_type, [...existing, result]);
        }
        return grouped;
    }, [response]);

    return (
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
            <div className="space-y-2">
                <h1 className="text-2xl font-semibold">Search</h1>
                <p className="text-muted-foreground text-sm">
                    Auto-searches all entity types across both live and sandbox
                    indexes.
                </p>
            </div>
            <Card className="space-y-5 p-5">
                <div className="space-y-2">
                    <Label htmlFor="admin-search-query">Query</Label>
                    <Textarea
                        id="admin-search-query"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        className="min-h-32"
                        placeholder="Search across all indexed entities..."
                    />
                </div>
                <div className="space-y-2">
                    <Label>Organization</Label>
                    <OrganizationCombobox
                        value={selectedOrganizationIds}
                        onValueChange={(ids) => {
                            setSelectedOrganizationIds(ids.slice(0, 1));
                        }}
                        placeholder="Select one organization..."
                        disabled={isSearching}
                    />
                </div>
                {!canSearch && (
                    <p className="text-muted-foreground text-xs">
                        Add query text and choose an organization to search.
                    </p>
                )}
            </Card>
            {errorMessage && (
                <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                    {errorMessage}
                </Card>
            )}
            {response !== null && !errorMessage && (
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">
                            Live: {response.counts_by_mode.live}
                        </Badge>
                        <Badge variant="outline">
                            Sandbox: {response.counts_by_mode.sandbox}
                        </Badge>
                        {response.entity_types.map((entityType) => (
                            <Badge key={entityType} variant="secondary">
                                {ENTITY_LABELS[entityType]}:{' '}
                                {response.counts_by_entity[entityType] ?? 0}
                            </Badge>
                        ))}
                    </div>
                    {Object.entries(response.errors_by_entity).length > 0 && (
                        <Card className="space-y-2 border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/50">
                            <p className="text-sm font-medium text-amber-900 dark:text-amber-300">
                                Some mode/entity queries failed
                            </p>
                            <div className="space-y-1 text-xs text-amber-800 dark:text-amber-300">
                                {Object.entries(response.errors_by_entity).map(
                                    ([scope, message]) => (
                                        <p key={scope}>
                                            {scope}: {message}
                                        </p>
                                    ),
                                )}
                            </div>
                        </Card>
                    )}
                    {response.results.length === 0 ? (
                        <Card className="text-muted-foreground p-6 text-sm">
                            No matches found.
                        </Card>
                    ) : (
                        <div className="space-y-6">
                            {response.entity_types.map((entityType) => {
                                const entityResults =
                                    resultsByEntity.get(entityType) ?? [];

                                if (entityResults.length === 0) {
                                    return null;
                                }

                                return (
                                    <section
                                        key={entityType}
                                        className="space-y-3"
                                    >
                                        <h2 className="text-base font-semibold">
                                            {ENTITY_LABELS[entityType]}
                                        </h2>
                                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                                            {entityResults.map((result) => {
                                                const title =
                                                    getResultTitle(result);
                                                const subtitle =
                                                    getResultSubtitle(result);
                                                const snippet =
                                                    getResultSnippet(result);
                                                return (
                                                    <Card
                                                        key={`${result.mode}:${result.entity_type}:${result.id}:${result.source_document_id ?? ''}`}
                                                        className="space-y-2 p-4"
                                                    >
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0 flex-1">
                                                                <p className="truncate text-sm font-semibold">
                                                                    {title}
                                                                </p>
                                                                {subtitle && (
                                                                    <p className="text-muted-foreground truncate text-xs">
                                                                        {
                                                                            subtitle
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <Badge variant="outline">
                                                                    {
                                                                        result.mode
                                                                    }
                                                                </Badge>
                                                                {result.score !==
                                                                    null &&
                                                                    result.score !==
                                                                        undefined && (
                                                                        <Badge
                                                                            variant="outline"
                                                                            className="shrink-0"
                                                                        >
                                                                            {result.score.toFixed(
                                                                                3,
                                                                            )}
                                                                        </Badge>
                                                                    )}
                                                            </div>
                                                        </div>
                                                        {snippet && (
                                                            <p className="text-muted-foreground line-clamp-4 text-sm whitespace-pre-wrap">
                                                                {snippet}
                                                            </p>
                                                        )}
                                                        <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
                                                            <span className="truncate">
                                                                id: {result.id}
                                                            </span>
                                                            {result.link && (
                                                                <a
                                                                    href={
                                                                        result.link
                                                                    }
                                                                    onClick={() => {
                                                                        modeStore.setMode(
                                                                            result.mode,
                                                                        );
                                                                    }}
                                                                    className="inline-flex items-center gap-1 hover:underline"
                                                                >
                                                                    Open
                                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                                </a>
                                                            )}
                                                        </div>
                                                    </Card>
                                                );
                                            })}
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            {isSearching && (
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                    <Spinner />
                    Searching live and sandbox indexes…
                </div>
            )}
        </div>
    );
}

function createEmptyCountsByEntity(): Record<SearchEntityType, number> {
    return {
        caregivers: 0,
        voice_calls: 0,
        messages: 0,
        shifts: 0,
        clients: 0,
    };
}

function combineSearchResponses({
    responses,
}: {
    readonly responses: ReadonlyArray<{
        readonly mode: SearchMode;
        readonly data: AdminSearchResponse | null;
        readonly error: string | null;
    }>;
}): CombinedSearchResponse {
    const results: Array<CombinedSearchResult> = [];
    const countsByEntity = createEmptyCountsByEntity();
    const countsByMode: Record<SearchMode, number> = {
        live: 0,
        sandbox: 0,
    };
    const errorsByEntity: Record<string, string> = {};

    for (const { mode, data, error } of responses) {
        if (data !== null) {
            for (const result of data.results) {
                results.push({ ...result, mode });
            }
            countsByMode[mode] = data.results.length;
            for (const entityType of ALL_ENTITY_TYPES) {
                countsByEntity[entityType] +=
                    data.counts_by_entity[entityType] ?? 0;
            }
            for (const [entityType, message] of Object.entries(
                data.errors_by_entity,
            )) {
                errorsByEntity[`${mode}:${entityType}`] = message;
            }
        }
        if (error !== null) {
            errorsByEntity[`${mode}:request`] = error;
        }
    }

    return {
        entity_types: ALL_ENTITY_TYPES,
        results,
        counts_by_entity: countsByEntity,
        counts_by_mode: countsByMode,
        errors_by_entity: errorsByEntity,
    };
}

async function fetchModeSearch({
    mode,
    query,
    organizationId,
}: {
    readonly mode: SearchMode;
    readonly query: string;
    readonly organizationId: string;
}): Promise<{
    readonly mode: SearchMode;
    readonly data: AdminSearchResponse | null;
    readonly error: string | null;
}> {
    try {
        const data = await getModeApiClient(mode).get({
            url: '/admin/search',
            queryParams: {
                q: query,
                organization_id: organizationId,
                mode,
                per_entity_limit: 8,
            },
            reason: 'admin-search',
        });
        return {
            mode,
            data,
            error: null,
        };
    } catch (error: unknown) {
        return {
            mode,
            data: null,
            error: getErrorMessage(error),
        };
    }
}

function getModeApiClient(mode: SearchMode): ApiClient {
    const existingClient = modeApiClientByMode.get(mode);
    if (existingClient) {
        return existingClient;
    }
    const modeClient = createApiClient({
        baseUrl: getRuntimeConfig().apiUrl,
        fetch: (url, init) => fetch(url, { ...init, credentials: 'include' }),
        getMode: () => mode,
    });
    modeApiClientByMode.set(mode, modeClient);
    return modeClient;
}

function getErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message.trim().length > 0) {
        return error.message;
    }
    return 'Search request failed.';
}

function asTrimmedString(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

function getHydratedField(
    result: SearchResult,
    key: string,
): unknown | undefined {
    return result.hydrated?.[key];
}

function getResultTitle(result: SearchResult): string {
    if (result.entity_type === 'caregivers') {
        return (
            asTrimmedString(getHydratedField(result, 'name')) ?? result.title
        );
    }
    if (result.entity_type === 'voice_calls') {
        return (
            asTrimmedString(getHydratedField(result, 'call_summary')) ??
            result.title
        );
    }
    if (result.entity_type === 'shifts') {
        return (
            asTrimmedString(getHydratedField(result, 'patient_name')) ??
            result.title
        );
    }
    if (result.entity_type === 'clients') {
        const preferredName = asTrimmedString(
            getHydratedField(result, 'preferred_name'),
        );
        if (preferredName) {
            return preferredName;
        }
        const firstName = asTrimmedString(
            getHydratedField(result, 'first_name'),
        );
        const lastName = asTrimmedString(getHydratedField(result, 'last_name'));
        const fullName = [firstName, lastName].filter(Boolean).join(' ');
        if (fullName.length > 0) {
            return fullName;
        }
    }
    return result.title;
}

function getResultSubtitle(result: SearchResult): string | null {
    if (result.entity_type === 'voice_calls') {
        const callStatus = asTrimmedString(
            getHydratedField(result, 'call_status'),
        );
        const direction = asTrimmedString(
            getHydratedField(result, 'direction'),
        );
        if (callStatus || direction) {
            return [callStatus, direction].filter(Boolean).join(' • ');
        }
    }
    if (result.entity_type === 'messages') {
        const role = asTrimmedString(getHydratedField(result, 'role'));
        const conversationId = asTrimmedString(
            getHydratedField(result, 'conversation_id'),
        );
        if (role || conversationId) {
            return `${role ?? 'message'} • convo ${conversationId ?? '?'}`;
        }
    }
    return result.subtitle ?? null;
}

function getResultSnippet(result: SearchResult): string | null {
    if (result.entity_type === 'messages') {
        return (
            asTrimmedString(getHydratedField(result, 'content')) ??
            result.snippet ??
            null
        );
    }
    if (result.entity_type === 'voice_calls') {
        return (
            asTrimmedString(getHydratedField(result, 'call_summary')) ??
            result.snippet ??
            null
        );
    }
    return result.snippet ?? null;
}
